import { FeatureSharedModule } from '@icullen/feature/shared';

import { BrowserModule } from '@angular/platform-browser';
import { NgModule, APP_INITIALIZER, ErrorHandler } from '@angular/core';
import { UiModule } from '@icullen/ui';
import { AppComponent } from './app.component';
import { RouterModule, ExtraOptions } from '@angular/router';

import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  TranslocoModule,
  TRANSLOCO_CONFIG,
  TranslocoConfig,
} from '@jsverse/transloco';
import { ErrorComponent } from '@icullen/ui';
import { ComingSoonComponent } from '@icullen/ui';
import {
  ApiSharedModule,
  ConfigAssetLoaderService,
  FavoriteService,
  AuthGuard,
  SsoCustomStorageService,
  FeatureFlagService,
  Configuration,
  AuthService,
} from '@icullen/api/shared';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { environment } from '@env/environment';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
//import Amplify, { Auth } from 'aws-amplify';
// import awsconfig from '../aws-exports';
// import Analytics from '@aws-amplify/analytics';
//import Auth from '@aws-amplify/auth';
import awsmobile from '../aws-exports_copy';
import {
  amplifyConfigDev,
  amplifyConfigProd,
  amplifyConfigTest,
} from '../amplify-config';
import { OAuthModule, OAuthService, OAuthStorage } from 'angular-oauth2-oidc';
import { authConfig } from './auth.config';
import {
  ActivateAccountComponent,
  SelfRegisterFormComponent,
} from '@icullen/public';
import { PublicModule } from '@icullen/public';
import { RecaptchaV3Module, RECAPTCHA_V3_SITE_KEY } from 'ng-recaptcha';
import {
  HttpCacheInterceptorModule,
  useHttpCacheLocalStorage,
} from '@ngneat/cashew';
import { ConnectivityGuard } from '@icullen/utils';
import { TranslocoRootModule } from './transloco-root.module';
import { VersionInfoComponent } from './app-version-info.component';

const routerOptions: ExtraOptions = {
  scrollPositionRestoration: 'enabled',
  anchorScrolling: 'enabled',
  onSameUrlNavigation: 'reload',
};

// export function initUserFavDocs(
//   favoriteService: FavoriteService,
//   configService: ConfigAssetLoaderService
// ) {
//   return () => {
//     configService
//       .getConfigObservable()
//       .subscribe(config => favoriteService.loadFavoriteDocs(config.apiBaseUrl));
//   };
// }

export function initConfigForEnvironment(
  configService: ConfigAssetLoaderService,
  oauthService: OAuthService,
  authService: AuthService
) {
  return () => {
    return configService
      .getConfigObservable()
      .toPromise()
      .then((config) => {
        //amplify config
        //initAmplifyConfig(config?.environment);

        if (config?.environment && config?.environment === 'production') {
          //if (config?.environment) {
          initSentryConfig(config?.environment);
        }

        const oAuthConfig = { ...authConfig }; // Clone to avoid mutation
        oAuthConfig.issuer = config.ssoIssuer;
        oAuthConfig.logoutUrl =
          config.ssoIssuer + '/protocol/openid-connect/logout';
        oAuthConfig.redirectUri =
          oAuthConfig.redirectUri + config?.deployBaseHref;

        oauthService.configure(oAuthConfig);
        oauthService.setupAutomaticSilentRefresh();
        authService.setupAuthEvents();
        console.log('loading discovery doc');
        return oauthService.loadDiscoveryDocumentAndTryLogin();
      });
  };
}

// function initAmplifyConfig(environment: string) {
//   if (environment === 'production') {
//     //Amplify.Logger.LOG_LEVEL = 'DEBUG';
//     Amplify.configure(amplifyConfigProd);
//     // Amplify.configure(amplifyAnalyticsConfig);
//     // Amplify.configure(amplifyConfig);
//     //Amplify.configure(awsmobile);
//   } else if (environment === 'test') {
//     //Amplify.Logger.LOG_LEVEL = 'DEBUG';
//     Amplify.configure(amplifyConfigTest);
//   } else {
//     //Amplify.Logger.LOG_LEVEL = 'DEBUG';
//     Amplify.configure(amplifyConfigDev);
//   }
// }

function initSentryConfig(environment: string) {
  Sentry.init({
    dsn: 'https://aec8b5e3cc094fd7907fc8f1ec4a1b1c@o525949.ingest.sentry.io/5640799',
    environment: environment ? environment : 'local',
    integrations: [
      Sentry.browserTracingIntegration(),
      // Registers the Replay integration,
      // which automatically captures Session Replays
      Sentry.replayIntegration(),
    ],
    tracePropagationTargets: [
      'https://www4.test.k8s.bigcct.be/api',
      'https://www.cullen-international.com/api',
    ],
    tracesSampleRate: 0.5,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event, hint) {
      // Check if the exception exists and has a stack trace
      if (event.exception?.values?.[0]?.stacktrace?.frames) {
        const frames = event.exception.values[0].stacktrace.frames;
        // Check if any frame originated from node_modules
        const isInNodeModules = frames.some((frame) =>
          frame.filename?.includes('node_modules')
        );

        // If the error is from node_modules, don't send it to Sentry
        if (isInNodeModules) {
          return null; // Returning null will tell Sentry to discard the event
        }
      }

      // Otherwise, return the event for it to be sent to Sentry
      return event;
    },
  });
}

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    UiModule,
    ApiSharedModule,
    FeatureSharedModule,
    PublicModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    TranslocoRootModule,
    HttpCacheInterceptorModule.forRoot({ ttl: 15 * 60 * 1000 }),
    OAuthModule.forRoot({
      resourceServer: {
        sendAccessToken: true,
      },
    }),
    RouterModule.forRoot(
      [
        //TODO By default redirect to global. remove this path in future when we do one site
        {
          path: '',
          redirectTo: '/site',
          pathMatch: 'full',
        },
        {
          path: 'global',
          //redirectTo: 'site/headlines/service/global_trends',
          canActivate: [ConnectivityGuard, AuthGuard],
          runGuardsAndResolvers: 'always',
          loadChildren: () =>
            import('@icullen/feature/global').then(
              (module) => module.FeatureGlobalModule
            ),
        },
        {
          path: 'site',
          canActivate: [ConnectivityGuard, AuthGuard],
          runGuardsAndResolvers: 'always',
          loadChildren: () =>
            import('@icullen/feature/site-viewer').then(
              (module) => module.FeatureSiteViewerModule
            ),
        },
        {
          path: 'trainings',
          canActivate: [ConnectivityGuard, AuthGuard],
          loadChildren: () =>
            import('@icullen/feature/trainings').then(
              (module) => module.FeatureTrainingsModule
            ),
        },
        {
          path: 'preview',
          canActivate: [ConnectivityGuard, AuthGuard],
          loadChildren: () =>
            import('@icullen/feature/document-preview').then(
              (module) => module.FeatureDocumentPreviewModule
            ),
        },
        {
          path: 'register',
          canActivate: [ConnectivityGuard],
          component: SelfRegisterFormComponent,
          pathMatch: 'full',
        },
        {
          path: 'activateAccount',
          component: ActivateAccountComponent,
          pathMatch: 'full',
        },
        // fixing local env links from icullen-webapp
        {
          path: 'client/site/documents/:id',
          redirectTo: 'site/documents/:id',
        },
        {
          path: 'error',
          component: ErrorComponent,
          pathMatch: 'full',
        },
        {
          path: '404',
          component: ErrorComponent,
          // data: {
          //   errorStatus: '404'
          // },
          pathMatch: 'full',
        },
        {
          path: '401',
          component: ErrorComponent,
          // data: {
          //   errorStatus: '401'
          // },
          pathMatch: 'full',
        },
        {
          path: 'coming-soon',
          component: ComingSoonComponent,
          pathMatch: 'full',
        },
        {
          path: '**',
          redirectTo: '/404',
        },
      ],
      routerOptions
    ),
    TranslocoModule,
    BrowserAnimationsModule,
    RecaptchaV3Module,
    VersionInfoComponent,
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: (configService: ConfigAssetLoaderService) => () =>
        configService.loadConfigurations().toPromise(),
      deps: [ConfigAssetLoaderService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initConfigForEnvironment,
      deps: [ConfigAssetLoaderService, OAuthService, AuthService],
      multi: true,
    },
    {
      provide: OAuthStorage,
      useClass: SsoCustomStorageService,
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
      useValue: undefined,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    {
      provide: RECAPTCHA_V3_SITE_KEY,
      useValue: environment.recaptchaSiteKey,
    },
    // {
    //   provide: APP_INITIALIZER,
    //   useFactory: initUserFavDocs,
    //   deps: [FavoriteService, ConfigAssetLoaderService],
    //   multi: true
    // },
    useHttpCacheLocalStorage,
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
